/** Намерения пользователя и выбор «Первых шагов». Чистая логика без React. */

export const MAIN_INTENTS = ["attend", "organize", "community", "expert"] as const;
export const EXTRA_INTENTS = ["meet", "advice", "hire", "market"] as const;
export type MainIntent = (typeof MAIN_INTENTS)[number];
export type ExtraIntent = (typeof EXTRA_INTENTS)[number];
export type PlatformIntent = MainIntent | ExtraIntent;

const ALL: readonly string[] = [...MAIN_INTENTS, ...EXTRA_INTENTS];

/** Только допустимые значения, без повторов. */
export function sanitizeIntents(values: readonly unknown[] | null | undefined): PlatformIntent[] {
  const out: PlatformIntent[] = [];
  for (const v of values ?? []) {
    if (typeof v === "string" && ALL.includes(v) && !out.includes(v as PlatformIntent)) {
      out.push(v as PlatformIntent);
    }
  }
  return out;
}

/** Приоритет основного набора. */
export const MAIN_PRIORITY: MainIntent[] = ["expert", "organize", "community", "attend"];

export type FirstStepId =
  | "events_open"
  | "install"
  | "push"
  | "event_create"
  | "event_share"
  | "circle_create"
  | "circle_share"
  | "expert_card"
  | "circle_join"
  | "meet_profile"
  | "ask"
  | "hire"
  | "market_open"
  | "expert_request"
  | "hosts_enable"
  | "circle_experts";

/** Основа — всем и в этом порядке. */
export const BASE_STEPS: FirstStepId[] = ["meet_profile", "install", "push"];

/** Шаг роли и шаги продолжения (появляются после выполнения шага роли). */
const SETS: Record<MainIntent, { role: FirstStepId; next: FirstStepId[] }> = {
  expert: { role: "expert_card", next: ["circle_join", "expert_request"] },
  organize: { role: "event_create", next: ["event_share", "hosts_enable"] },
  community: { role: "circle_create", next: ["circle_share", "circle_experts"] },
  attend: { role: "events_open", next: [] },
};

const EXTRA_STEP: Record<ExtraIntent, FirstStepId> = {
  meet: "meet_profile",
  advice: "ask",
  hire: "hire",
  market: "market_open",
};

export const FIRST_STEPS_LIMIT = 8;

/**
 * Три слоя: основа (профиль → установка → уведомления), шаги ролей по приоритету
 * expert → organize → community → attend (по умолчанию attend), продолжение роли —
 * только после выполнения её шага. Затем доп. плашки. Не больше FIRST_STEPS_LIMIT.
 */
export function pickFirstSteps(
  intents: readonly PlatformIntent[],
  done: Partial<Record<FirstStepId, boolean>> = {},
): FirstStepId[] {
  const mains = MAIN_PRIORITY.filter((m) => intents.includes(m));
  if (mains.length === 0) mains.push("attend");
  const list: FirstStepId[] = [...BASE_STEPS];
  for (const m of mains) {
    list.push(SETS[m].role);
    if (done[SETS[m].role]) list.push(...SETS[m].next);
  }
  for (const e of EXTRA_INTENTS) if (intents.includes(e)) list.push(EXTRA_STEP[e]);
  return [...new Set(list)].slice(0, FIRST_STEPS_LIMIT);
}

/** Индекс текущего (первого невыполненного) шага; -1 — всё выполнено. */
export function currentStepIndex(steps: FirstStepId[], done: Partial<Record<FirstStepId, boolean>>): number {
  return steps.findIndex((s) => !done[s]);
}
